import { faPen, faTrash } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ConfigurationPageHeader } from '@modules/configuration/components/ConfigurationPageHeader';
import {
  configurationPageClass,
  configurationPageContentClass,
} from '@modules/configuration/components/configurationLayout';
import { Button } from '@components/ui/button/Button';
import { ProductCategory } from '../../models/ProductCategory';
import { FiltersSection } from '../interaction';
import { CategoriesEmptyState } from './CategoriesEmptyState';

interface CategoriesTableProps {
  categories: ProductCategory[];
  search: string;
  onSearchChange: (value: string) => void;
  canManage: boolean;
  onCreateClick: () => void;
  onEdit: (category: ProductCategory) => void;
  onDelete: (category: ProductCategory) => void;
}

const TABLE_GRID_WITH_ACTIONS = 'grid-cols-[minmax(0,1fr)_5.5rem_5.5rem]';
const TABLE_GRID_WITHOUT_ACTIONS = 'grid-cols-[minmax(0,1fr)_5.5rem]';

export function CategoriesTable({
  categories,
  search,
  onSearchChange,
  canManage,
  onCreateClick,
  onEdit,
  onDelete,
}: CategoriesTableProps) {
  const { t } = useTranslation('productCategoriesModule');
  const [searchInitialValue] = useState(() => search);
  const hasSearch = search.trim().length > 0;
  const hasCategories = categories.length > 0;
  const showToolbar = hasCategories || hasSearch;
  const gridColsClass = canManage ? TABLE_GRID_WITH_ACTIONS : TABLE_GRID_WITHOUT_ACTIONS;

  return (
    <div className={configurationPageClass} data-testid="product-categories-view">
      <div className="shrink-0">
        <ConfigurationPageHeader
          title={t('pageTitle')}
          description={t('pageDescription')}
          action={
            canManage && hasCategories ? (
              <Button variant="primary" size="default" onClick={onCreateClick} data-testid="create-category-button">
                <Plus className="h-4 w-4" aria-hidden />
                {t('buttons.create')}
              </Button>
            ) : undefined
          }
        />

        {showToolbar && (
          <div className="mt-8">
            <FiltersSection initialValue={searchInitialValue} onSearchChange={onSearchChange} />
          </div>
        )}
      </div>

      <div className={`${configurationPageContentClass} flex flex-col overflow-hidden pb-6`}>
        {!hasCategories ? (
          <CategoriesEmptyState onCreateClick={canManage ? onCreateClick : undefined} hasSearch={hasSearch} />
        ) : (
          <div className="flex min-h-0 flex-1 w-full flex-col overflow-hidden rounded-lg border border-border bg-background">
            <div
              className={`grid shrink-0 items-center gap-4 border-b border-border bg-muted/50 px-4 py-3 text-sm font-medium text-muted-foreground ${gridColsClass}`}
            >
              <span>{t('table.name')}</span>
              <span className="text-center">{t('table.batchRequired')}</span>
              {canManage && <span className="text-right">{t('table.actions')}</span>}
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain pb-px">
              {categories.map((category, index) => (
                <div
                  key={category.id}
                  className={`grid items-center gap-4 px-4 py-3 text-sm transition-colors hover:bg-muted/30 ${gridColsClass} ${
                    index < categories.length - 1 ? 'border-b border-border' : ''
                  }`}
                  data-testid={`category-row-${category.id}`}
                >
                  <span className="font-medium text-foreground">{category.name}</span>
                  <span
                    className={`text-center text-sm ${
                      category.batch_required ? 'font-medium text-foreground' : 'text-muted-foreground'
                    }`}
                  >
                    {category.batch_required ? t('batchRequired.yes') : t('batchRequired.no')}
                  </span>
                  {canManage && (
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onEdit(category)}
                        data-testid={`edit-category-${category.id}`}
                      >
                        <FontAwesomeIcon icon={faPen} className="!h-3.5 !w-3.5" />
                        <span className="sr-only">{t('buttons.edit')}</span>
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onDelete(category)}
                        className="text-destructive hover:text-destructive"
                        data-testid={`delete-category-${category.id}`}
                      >
                        <FontAwesomeIcon icon={faTrash} className="!h-3.5 !w-3.5" />
                        <span className="sr-only">{t('buttons.delete')}</span>
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
